import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'

import { Field, FieldData, FieldDataValue, FieldType } from 'embed/constants'
import { ValidationData } from 'embed/services/Validation'

import ThemeConsumer from 'embed/components/ThemeConsumer'
import { FieldTooltip } from './FieldTooltip'
import { FloatingLabel } from './FloatingLabel'
import { InputField } from './InputField'
import { Label } from './Label'
import { ValidationError } from './ValidationError'

const styles = require('embed/styles/application')
const applicationFieldStyles = require('./styles')
const filterStyles = require('embed/styles/filters')

export interface Props {
  borderRadiusSmall: string
  colorCta: string
  colorError: string
  colorTextSecondary: string
  field: Field
  fieldData?: FieldData
  fontFamilyPrimary: string
  fontSizeBase: string
  onAddressSelect: (addressOne: string, city: string, state: string, zip: string) => void
  onBlur: (value: React.FormEvent<HTMLInputElement>) => void
  onChange: (value: FieldDataValue) => void
  onKeyDown: (key: string) => void
  onUniversitySelect: (name: string) => void
  forceFocus?: boolean
  tooltip?: React.ReactNode
  styles?: string
  validationData: ValidationData
  useFloatingLabelInputs?: boolean
}

const ApplicationField: React.FC<Props> = (props) => {
  const { useFloatingLabelInputs } = props

  const fieldValue = props.fieldData?.value ?? ''
  const isLoading = props.fieldData?.isLoading ?? false
  const isValid = props.fieldData?.isValid ?? false
  const shouldShowIsInvalid = props.fieldData?.shouldShowValidationStatus === true && isValid === false

  const hideTooltip = useFloatingLabelInputs && props.field.type === FieldType.ButtonList
  let tooltip
  if (props.field.options.tooltip && !hideTooltip) {
    tooltip = (
      <FieldTooltip
        field={props.field}
        useFloatingLabelInputs={useFloatingLabelInputs}
        shouldShowIsInvalid={shouldShowIsInvalid}
      />
    )
  }

  const additionalStyles = props.styles

  return (
    <div
      className={classnames(styles.formRow, additionalStyles, {
        [filterStyles.isLoading]: isLoading,
        [css`
          &:after {
            right: 29px;
          }
        `]: isLoading && props.field.type === FieldType.Select,
        [css`
          &:after {
            border-right-color: ${props.colorCta};
          }
        `]: isLoading,
        [applicationFieldStyles.floatingLabelContainer]: useFloatingLabelInputs,
      })}
    >
      <Label tooltip={tooltip} useFloatingLabelInputs={useFloatingLabelInputs} {...props} />
      <InputField {...props} tooltip={tooltip} />
      <FloatingLabel
        useFloatingLabelInputs={useFloatingLabelInputs}
        fieldOptions={props.field.options}
        tooltip={tooltip}
      />
      <ValidationError
        shouldShowIsInvalid={shouldShowIsInvalid}
        validationMessage={props.field.validationMessage(fieldValue, props.validationData)}
      />
      {props.children}
    </div>
  )
}

export const ApplicationFieldContainer = (props) => (
  <ThemeConsumer>
    {({ borderRadiusSmall, ctaStyles, colorTextSecondary, errorStyles, fontFamilyPrimary, fontSizeBase }) => (
      <ApplicationField
        borderRadiusSmall={borderRadiusSmall}
        colorCta={ctaStyles?.backgroundColor}
        colorError={errorStyles?.color}
        colorTextSecondary={colorTextSecondary}
        fontFamilyPrimary={fontFamilyPrimary}
        fontSizeBase={fontSizeBase}
        {...props}
      />
    )}
  </ThemeConsumer>
)

export const ApplicationFieldView = ApplicationFieldContainer
